import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getIDFromUrl } from '@rossum/api-client';
import { MetaField } from '@rossum/api-client/shared';
import { GridSortItem } from '@rossum/components/data-grid/utils';
import { LinearProgress, Paper, Stack } from '@rossum/ui/material';
import {
  GridFilterItem,
  GridPaginationModel,
  GridSortModel,
} from '@rossum/ui/x-data-grid-pro';
import TrialOnboarding from '../../containers/TrialOnboarding';
import { isTrialSelector } from '../../redux/modules/organization/selectors';
import { firstTimeUserSelector } from '../../redux/modules/user/selectors';
import {
  allowedPageSizes,
  DEFAULT_PAGE_SIZE,
  usePagination,
} from '../document-list-base/hooks/usePagination';
import {
  SelectAnnotationParams,
  supportedAnnotationViews,
} from '../document-list-base/supportedAnnotationViews';
import { LevelOptions } from '../document-list-base/types';
import { getUpdatedExistingFilter } from '../document-list-filtering/getUpdatedExistingFilter';
import { DocumentDrawer } from '../document/DocumentDrawer';
import { createNewFilterQuery } from '../mql/transformers';
import { AddQueueDialog } from '../queue-sidebar/add-queue/AddQueueDialog';
import { DashboardModalsState, Sidebar } from '../queue-sidebar/sidebar';
import { AddWorkspaceModal } from '../queue-sidebar/sidebar/components/AddWorkspaceModal';
import { useWorkspacesWithQueues } from '../queues/hooks/useWorkspacesWithQueues';
import { DocumentsContent } from './DocumentsContent';
import { EmailsContent } from './EmailsContent';
import { getActiveQueue, getPageSizeFromQuery } from './helpers';
import { useIsUserRestrictedToAllDocsLevel } from './hooks/useAreViewersRestricted';
import { DashboardQuery, useDashboardQuery } from './hooks/useDashboardQuery';
import { useInvalidateDashboardData } from './hooks/useFetchDashboardData';
import { useSidebarState } from './hooks/useSidebarState';
import { useValidateDashboardLevel } from './hooks/useValidateDashboardLevel';
import { AnnotationActionsContext } from './selection-panel/AnnotationActionContext';
import { useStatusTabs } from './statuses/hooks/useStatusTabs';
import { getMatchingStatusFilter } from './statuses/utils';

const CONTENT_PADDING = 2;

const QUEUE_COLUMN: MetaField = 'queue';

const encodeSortModel = (sortColumn: GridSortItem) =>
  `${sortColumn.sort === 'desc' ? '-' : ''}${sortColumn.field}`;

const decodeSortModel = (ordering: string): GridSortItem[] =>
  ordering.startsWith('-')
    ? [{ field: ordering.slice(1), sort: 'desc' as const }]
    : [{ field: ordering, sort: 'asc' as const }];

const DocumentList = () => {
  const { isUserRestrictedToAllDocsLevel } =
    useIsUserRestrictedToAllDocsLevel();

  const [dashboardState, setDashboardState] = useState<DashboardModalsState>({
    addingQueue: false,
    addingWorkspace: false,
    dialogResetCounter: 0,
  });

  const { isSidebarOpen, toggleSidebar } = useSidebarState();

  const { isLoading, queues } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const {
    query,
    setQuery,
    view,
    setView,
    currentQueueId,
    level,
    existingFilter,
  } = useDashboardQuery();

  const queryOrdering = query['ordering'];
  const querySearch = query['search'];
  const queryLevel = query['level'];

  const activeQueue = getActiveQueue({
    level: level ?? undefined,
    queues,
    queueId: currentQueueId,
  });

  const activeQueueName = activeQueue?.name ?? null;

  const statusTabs = useStatusTabs(activeQueue);
  const activeTabKey =
    getMatchingStatusFilter({
      existingFilterItems: existingFilter?.items ?? [],
      statusTabs,
    })?.key ?? null;

  useEffect(() => {
    if (isUserRestrictedToAllDocsLevel && level !== 'all') {
      setQuery(prev => ({ ...prev, level: 'all' }));
    } else if (level === 'initial' && !currentQueueId && queues) {
      const firstQueueId = queues[0]?.id;

      if (firstQueueId) {
        setQuery(prevQuery => ({
          ...prevQuery,
          level: 'queue',
          filtering: createNewFilterQuery([
            {
              field: QUEUE_COLUMN,
              operator: 'isAnyOf',
              value: [`${firstQueueId}`],
            },
          ]),
        }));
      } else {
        setQuery(prev => ({ ...prev, level: 'all' }));
      }
    }
  }, [level, currentQueueId, setQuery, queues, isUserRestrictedToAllDocsLevel]);

  useValidateDashboardLevel({
    isUserRestrictedToAllDocsLevel,
    existingFilter,
    levelQuery: query['level'],
    onFallback: validLevel =>
      setQuery(prev => ({ ...prev, level: validLevel })),
  });

  const onPaginationReset = useCallback(
    () => setQuery(prevQuery => ({ ...prevQuery, page: '1' })),
    [setQuery]
  );

  const onPaginationChange = useCallback(
    (newPaginationModel: GridPaginationModel) =>
      setQuery(prevQuery => ({
        ...prevQuery,
        page: `${newPaginationModel.page + 1}`,
        page_size: `${newPaginationModel.pageSize}`,
      })),
    [setQuery]
  );

  const paginationProps = usePagination({
    page: query['page'] ? parseInt(query['page'], 10) : null,
    pageSize: getPageSizeFromQuery({
      query,
      fallbackValue: DEFAULT_PAGE_SIZE,
      allowedPageSizes,
    }),
    onPaginationReset,
    onPaginationChange,
    disableResetOnMissingPage: view === 'emails',
  });

  const { resetPagination } = paginationProps;

  const onSortModelChange = ([sortColumn]: GridSortModel) =>
    handleQueryChange({
      ordering: sortColumn ? encodeSortModel(sortColumn) : undefined,
    });

  const sortModel: GridSortModel = useMemo(() => {
    if (!queryOrdering) {
      return [];
    }

    return decodeSortModel(queryOrdering);
  }, [queryOrdering]);

  const sortingProps = {
    sortModel,
    onSortModelChange,
  };

  const onSearchSubmit = (searchValue: string | undefined) =>
    handleQueryChange({ search: searchValue }, searchValue !== querySearch);

  const handleQueryChange = useCallback(
    (newQuery: DashboardQuery, shouldResetPagination = true) => {
      if (shouldResetPagination) resetPagination();

      setQuery(prevQuery => {
        const mergedQuery = {
          ...prevQuery,
          ...newQuery,
        };
        return Object.fromEntries(
          Object.entries(mergedQuery).filter(([_, value]) => Boolean(value))
        );
      });
    },
    [resetPagination, setQuery]
  );

  const handleFilterModelChange = useCallback(
    (newFilterItems: GridFilterItem[]) => {
      const shouldClearFilter = newFilterItems.length === 0;

      return handleQueryChange({
        filtering: shouldClearFilter
          ? undefined
          : createNewFilterQuery(newFilterItems),
      });
    },
    [handleQueryChange]
  );

  const handleClearAllFilters = useCallback(
    () =>
      handleFilterModelChange(
        existingFilter?.items.filter(filter => filter.field === queryLevel) ??
          []
      ),
    [existingFilter?.items, handleFilterModelChange, queryLevel]
  );

  const handleLevelChange = useCallback(
    (newLevel: LevelOptions) => {
      handleQueryChange({
        level: newLevel,
      });
    },
    [handleQueryChange]
  );

  const onQueueChange = (queueId: number) => {
    const incomingFilters: GridFilterItem[] = [
      {
        field: QUEUE_COLUMN,
        value: [`${queueId}`],
        operator: 'isAnyOf',
      },
    ];

    handleFilterModelChange(
      getUpdatedExistingFilter({
        existingFilterItems: existingFilter?.items ?? null,
        incomingFilters,
        isReplaceAction: existingFilter
          ? existingFilter.items.some(item => item.field === QUEUE_COLUMN)
          : false,
      })
    );

    handleLevelChange('queue');
  };

  const onAllDocsLevelClick = useCallback(() => {
    const filtersWithoutQueue = existingFilter?.items.filter(
      item => item.field !== QUEUE_COLUMN
    );
    handleQueryChange({
      filtering:
        filtersWithoutQueue && filtersWithoutQueue.length > 0
          ? createNewFilterQuery(filtersWithoutQueue)
          : undefined,
      view: undefined,
      level: 'all',
    });
  }, [handleQueryChange, existingFilter?.items]);

  const firstTimeUser = useSelector(firstTimeUserSelector);
  const isTrial = useSelector(isTrialSelector);
  const showTrialOnboarding = isTrial && firstTimeUser && !isLoading;

  const handleSelectAnnotation = useCallback(
    (params: SelectAnnotationParams) =>
      setQuery(prevQuery => ({
        ...prevQuery,
        selected_annotation: getIDFromUrl(params.annotationUrl).toString(),
        selected_annotation_view: params.view,
        selected_relation: params.relationId?.toString(),
      })),
    [setQuery]
  );

  const invalidateDashboardData = useInvalidateDashboardData();

  return (
    <Stack flexDirection="row" width={1} height={1} flex={1}>
      {!isUserRestrictedToAllDocsLevel && (
        <Sidebar
          currentQueueId={currentQueueId}
          isSidebarOpen={isSidebarOpen}
          setDashboardState={setDashboardState}
          onQueueChange={onQueueChange}
          setAllDocumentsLevelActive={onAllDocsLevelClick}
          level={level}
        />
      )}
      <Stack
        p={CONTENT_PADDING}
        flex={1}
        height={1}
        sx={{
          overflow: 'scroll',
          backgroundColor: t => t.palette.background.default,
        }}
      >
        <Paper sx={{ p: 2, height: '100%' }}>
          {level === 'initial' ? (
            // TODO: @Dual Don Skeleton will add a proper skeleton
            <LinearProgress />
          ) : view === 'emails' && level === 'queue' ? (
            <EmailsContent
              isSidebarEnabled={!isUserRestrictedToAllDocsLevel}
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={toggleSidebar}
              view={view}
              setView={setView}
              level={level}
              activeQueueId={currentQueueId}
              onPaginationChange={onPaginationChange}
              query={query}
              sortingProps={sortingProps}
              onSearchSubmit={onSearchSubmit}
              handleFilterModelChange={handleFilterModelChange}
              handleClearAllFilters={handleClearAllFilters}
            />
          ) : (
            <AnnotationActionsContext
              activeQueueName={activeQueueName}
              activeTabKey={activeTabKey}
              queues={queues}
            >
              <DocumentDrawer
                onClose={() => {
                  invalidateDashboardData();
                  setQuery(prevQuery => ({
                    ...prevQuery,
                    // Reset annotation and view
                    selected_annotation: undefined,
                    selected_annotation_view: undefined,
                    selected_relation: undefined,
                  }));
                }}
                selectedAnnotationId={
                  Number(query['selected_annotation']) || undefined
                }
                selectedAnnotationView={supportedAnnotationViews.find(
                  supportedAnnotationView =>
                    supportedAnnotationView ===
                    query['selected_annotation_view']
                )}
                selectedRelationId={
                  Number(query['selected_relation']) || undefined
                }
                handleSelectAnnotation={handleSelectAnnotation}
              />
              <DocumentsContent
                isSidebarEnabled={!isUserRestrictedToAllDocsLevel}
                activeQueueId={currentQueueId}
                paginationProps={paginationProps}
                handleQueryChange={handleQueryChange}
                handleFilterModelChange={handleFilterModelChange}
                handleClearAllFilters={handleClearAllFilters}
                query={query}
                handleSelectAnnotation={handleSelectAnnotation}
                isSidebarOpen={isSidebarOpen}
                toggleSidebar={toggleSidebar}
                view={view}
                setView={setView}
                level={level}
                sortingProps={sortingProps}
                onSearchSubmit={onSearchSubmit}
                openQueueModal={() =>
                  setDashboardState(prev => ({ ...prev, addingQueue: true }))
                }
              />
            </AnnotationActionsContext>
          )}
        </Paper>
      </Stack>
      {showTrialOnboarding && <TrialOnboarding />}
      {/* `dialogResetCounter` is for resetting internal states of dialogs without explicitly unmounting them */}
      <AddWorkspaceModal
        key={`workspace-${dashboardState.dialogResetCounter}`}
        open={dashboardState.addingWorkspace}
        onClose={() =>
          setDashboardState(state => ({ ...state, addingWorkspace: false }))
        }
      />
      <AddQueueDialog
        key={`queue-${dashboardState.dialogResetCounter}`}
        open={dashboardState.addingQueue}
        onClose={() =>
          setDashboardState(state => ({ ...state, addingQueue: false }))
        }
        onSuccess={onQueueChange}
        currentQueueId={currentQueueId}
      />
    </Stack>
  );
};

export { DocumentList };
